/**
 * WHO: the people in the story, their role, and what each said on the record,
 * newest first. Built in `boardroom/story.py`.
 */
import type { BoardroomPeople } from '../../../types';
import { CARD } from './tableLayout';
import { heardLabel } from './painTimeline';

export default function PeopleReport({ data, onOpen }: { data: BoardroomPeople; onOpen: (id: number) => void }) {
  if (data.rows.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Nobody in the story yet. The chronicler adds the people on the next dig.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3" data-testid="people-report">
      <p className="text-xs text-gray-400">
        {data.totals.people} {data.totals.people === 1 ? 'person' : 'people'} · {data.totals.on_record} on the record
      </p>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(18rem,1fr))] gap-3">
        {data.rows.map(p => (
          <section key={p.id} className={`${CARD} flex flex-col gap-1.5 p-3`}>
            <div className="flex items-baseline gap-2">
              <button
                type="button"
                onClick={() => onOpen(p.id)}
                className="text-left text-sm font-medium text-gray-900 hover:underline dark:text-gray-100"
              >
                {p.title}
              </button>
              {p.role && <span className="text-xs text-gray-500 dark:text-gray-400">{p.role}</span>}
              {p.events > 0 && (
                <span className="ml-auto text-[11px] text-gray-400">
                  {p.events} event{p.events === 1 ? '' : 's'}
                </span>
              )}
            </div>
            {p.quotes.length === 0 ? (
              <p className="text-xs italic text-gray-400">Nothing on the record yet.</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {p.quotes.slice(0, 3).map(q => (
                  <li key={q.id} className="text-xs text-gray-700 dark:text-gray-200">
                    “{q.quote}”{' '}
                    <span className="text-gray-400">
                      {q.said_at ? heardLabel(q.said_at) : 'date unknown'}
                      {q.url && (
                        <>
                          {' · '}
                          <a
                            href={q.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-indigo-600 hover:underline dark:text-indigo-300"
                          >
                            {q.url.replace(/^https?:\/\//, '').split('/')[0]}
                          </a>
                        </>
                      )}
                    </span>
                  </li>
                ))}
                {p.quotes.length > 3 && <li className="text-[11px] text-gray-400">+{p.quotes.length - 3} more</li>}
              </ul>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
